import { useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { CONTRACT_ROLES, type ContractRole, type CustomVariableDef, type Language } from "@talentkontor/domain";
import { api } from "../../../data";
import { useStaffWorkspace, type StaffWorkspace } from "../../../app/session";
import { betraegeAus, useBetraegeSichtbar } from "../../../app/betraege";
import { Dialog } from "../../../components/ui";
import { describeError } from "../../../components/States";
import { displayZone, today as todayIn } from "../../../lib/format";
import { t } from "../../../lib/sprache";
import { loadOrgDaten, loadVorlage, type OrgDaten, type VertragDetail } from "./daten";
import { VertragPapier, marksOf, type SignatureMark } from "./Papier";
import { SeitenDruck } from "./Seitenansicht";
import { exampleValues, renderDraft, vorlagenBeispiel } from "./pruefen";
import { geldFormat, kontextFuer, useVertragDaten } from "./useVertrag";
import { documentOf, renderCover } from "./dokument";
import { ROLE_LABEL, contractNumberLabel } from "./modell";
import { PROTOCOL_TEXT, type Fassung } from "./protokoll";
import {
  ausfertigungAusDetail, ausfertigungFileName, ausfertigungPdf, entwurfFileName, ENTWURF_VERMERK, saveFile, type AusfertigungInput, type AusfertigungSigner,
} from "./ausfertigung";
import "./vertraege.css";
import { geltendeVorlage } from "../../../model/beleg-vorlagen";

/**
 * Vorschau eines Vertrags als eigene Vollansicht (K-511, Inhaber 20.10.2026: „bei Verträgen kann ich nicht in die Vorschau
 * öffnen“): breites Overlay mit den Seiten wie im PDF, Seitenleiste, gesetzten Unterschriften und der Sicht je Partei (mit den
 * vertraulichen Teilen je Rolle). PDF und Druck gibt es auch für Entwürfe und Vorlagen, dann mit dem Vermerk „Entwurf – nicht
 * unterschrieben“ auf jeder Seite (K-513). Offen ist sie über ?vorschau=<id> in der Adresse: Zurück im Browser schließt sie.
 */

export const VORSCHAU_PARAM = "vorschau";
const VON_HIER = "tkVorschau";

/** Adresse mit gesetztem oder entferntem ?vorschau=<id>; andere Parameter (etwa ?schritt im Assistenten) bleiben. */
export function vorschauZiel(location: { pathname: string; search: string; hash: string }, wert: string | null): { pathname: string; search: string; hash: string } {
  const n = new URLSearchParams(location.search);
  if (wert) n.set(VORSCHAU_PARAM, wert);
  else n.delete(VORSCHAU_PARAM);
  const q = n.toString();
  return { pathname: location.pathname, search: q ? `?${q}` : "", hash: location.hash };
}

/** Schließen: einen Schritt zurück, wenn die Vorschau hier geöffnet wurde (eigener Verlaufseintrag), sonst Parameter entfernen. */
export function vorschauSchliessen(state: unknown): "zurueck" | "ersetzen" {
  return state && typeof state === "object" && (state as Record<string, unknown>)[VON_HIER] ? "zurueck" : "ersetzen";
}

/**
 * Vorschau über die Adresse (?vorschau=<id>): Öffnen legt einen Eintrag im Verlauf an, Zurück entfernt ihn und schließt die
 * Vorschau. Schließen per Knopf oder Esc geht denselben Schritt zurück; kam die Adresse von außen (Link, Suche), wird nur
 * der Parameter entfernt.
 */
export function useVorschauParam(): { id: string | null; open: (id: string) => void; close: () => void } {
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state && typeof location.state === "object" ? (location.state as Record<string, unknown>) : {};
  return {
    id: params.get(VORSCHAU_PARAM),
    open: (x: string) => navigate(vorschauZiel(location, x), { state: { ...state, [VON_HIER]: true } }),
    close: () => {
      if (vorschauSchliessen(location.state) === "zurueck") navigate(-1);
      else navigate(vorschauZiel(location, null), { replace: true, state: location.state });
    },
  };
}

/** Was die Vorschau zeigt: Text je Sicht, Unterschriften, Art (Entwurf, Vorlage mit Beispielwerten, versendeter Vertrag). */
export interface VorschauQuelle {
  title: string;
  numberLabel: string | null;
  lang: Language;
  /** Sichten, zwischen denen die Vorschau umschaltet (Agentur immer zuerst) */
  roles: readonly ContractRole[];
  /** Text je Sicht; der vertrauliche Teil fehlt in der Sicht einer Partei, vor der er verborgen ist */
  render: (view: ContractRole) => { html: string | null; annexHtml: string | null };
  hiddenFrom: readonly ContractRole[];
  /** gibt es einen vertraulichen Teil mit Inhalt? (nur dann der Hinweis in der Sicht der Partei) */
  hasAnnex: boolean;
  marks: readonly SignatureMark[];
  defs?: readonly CustomVariableDef[];
  art: "draft" | "template" | "contract";
  /** versendete Verträge: Ausfertigung je Fassung mit Unterschriften und Protokoll (derselbe Weg wie die Druckansicht) */
  ausfertigung?: (view: Fassung) => AusfertigungInput;
  /** Link zur vollständigen Seite (Liste, Kooperation, Stammdaten) */
  link?: string | null;
}

/** Unterzeichner fürs PDF eines Entwurfs: dieselben Felder wie im Papier, alle noch offen. */
function signersAusMarken(marks: readonly SignatureMark[]): AusfertigungSigner[] {
  return marks.map((m) => ({
    role: m.role, slot: m.slot ?? 1, capacity: m.capacity === "legal_guardian" ? "legal_guardian" : "party", name: m.name ?? null, signed: false,
    signing_name: null, signing_position: null, signing_authority: null, signed_at: null, signature_data: null,
  }));
}

/** Kopfzeile wie im PDF: Agentur · Nummer · Fassung. */
function kopfzeile(agencyName: string, q: VorschauQuelle, view: ContractRole): string {
  return [agencyName, q.numberLabel, PROTOCOL_TEXT[q.lang].fassung[view]].filter(Boolean).join(" · ");
}

/** Overlay der Vorschau; die Daten kommen fertig als Quelle (Editor, Versand, Detail, Vorlagen, Listen). */
export function VorschauDialog({ open, onClose, title, quelle, meldung }: {
  open: boolean;
  onClose: () => void;
  title: string;
  quelle: VorschauQuelle | null;
  /** statt der Vorschau: Laden, kein Zugriff, Fehler */
  meldung?: ReactNode;
}) {
  return (
    <Dialog open={open} onClose={onClose} title={title} wide>
      {open && (quelle ? <VorschauInhalt quelle={quelle} /> : <div className="tk-vv stack">{meldung}</div>)}
    </Dialog>
  );
}

function VorschauInhalt({ quelle }: { quelle: VorschauQuelle }) {
  const ws = useStaffWorkspace();
  const [sicht, setSicht] = useState<ContractRole>(quelle.roles[0] ?? "agency");
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [druck, setDruck] = useState(false);
  const view = quelle.roles.includes(sicht) ? sicht : quelle.roles[0] ?? "agency";
  const r = quelle.render(view);
  const tz = ws.org.timezone || displayZone();
  const agencyName = ws.org.name;
  const entwurf = quelle.art !== "contract";
  const vermerk = entwurf ? ENTWURF_VERMERK[quelle.lang] : null;
  const kopf = kopfzeile(agencyName, quelle, view);
  const verborgen = view !== "agency" && quelle.hasAnnex && quelle.hiddenFrom.includes(view);

  // Drucken (K-513): die Seiten wie im PDF kommen in einen eigenen Bereich am Ende der Seite; nur er wird gedruckt
  useEffect(() => {
    if (!druck) return;
    document.body.classList.add("tk-vv-drucken");
    const fertig = () => setDruck(false);
    window.addEventListener("afterprint", fertig);
    const timer = window.setTimeout(() => {
      try { window.print(); } catch { setDruck(false); }
    }, 60);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("afterprint", fertig);
      document.body.classList.remove("tk-vv-drucken");
    };
  }, [druck]);

  async function pdf() {
    if (busy) return;
    setBusy(true);
    setFehler(null);
    try {
      const marke = { logoPath: ws.org.logo_path, accent: ws.org.brand_color, layout: await geltendeVorlage(ws.org.org_id, "contract", null) };
      if (!entwurf && quelle.ausfertigung) {
        saveFile(await ausfertigungPdf({ ...quelle.ausfertigung(view), ...marke }), ausfertigungFileName(quelle.title, quelle.numberLabel, view, quelle.lang));
      } else {
        saveFile(await ausfertigungPdf({
          view, language: quelle.lang, title: quelle.title, numberLabel: quelle.numberLabel, agencyName, timeZone: tz, status: "draft",
          sentAt: null, completedAt: null, html: r.html, annexHtml: r.annexHtml, signers: signersAusMarken(quelle.marks), events: [],
          documentSha: null, finalSha: null, draft: quelle.art === "template" ? "sample" : "draft", ...marke,
        }), entwurfFileName(quelle.title, view, quelle.lang));
      }
    } catch (e) {
      setFehler(describeError(e).text);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="tk-vv stack">
      <div className="tk-vv-bar">
        {quelle.roles.length > 1 && (
          <span role="group" aria-label={t("Sicht von", "View as")} className="tk-vv-sicht">
            <span className="tk-vv-label" aria-hidden="true">{t("Sicht von", "View as")}</span>
            {quelle.roles.map((x) => <button key={x} type="button" aria-pressed={view === x} onClick={() => setSicht(x)}>{ROLE_LABEL[x]}</button>)}
          </span>
        )}
        <span className="tk-vv-aktionen">
          <button type="button" disabled={busy || !r.html} onClick={() => void pdf()}>{busy ? t("Wird erstellt …", "Creating …") : t("PDF herunterladen", "Download PDF")}</button>
          <button type="button" disabled={!r.html} onClick={() => setDruck(true)}>{t("Drucken", "Print")}</button>
          {quelle.link && <Link className="btn" to={quelle.link} replace>{t("Vollständig öffnen", "Open in full")}</Link>}
        </span>
      </div>
      {entwurf && (
        <p className="notice tk-vv-hinweis" role="note">
          <strong>{t("Entwurf – nicht unterschrieben.", "Draft – not signed.")}</strong>{" "}
          {quelle.art === "template"
            ? t("Vorlage mit Beispielwerten statt echter Stammdaten. PDF und Druck tragen den Vermerk auf jeder Seite.", "Template with sample values instead of real master data. PDF and print carry the note on every page.")
            : t("PDF und Druck tragen den Vermerk auf jeder Seite.", "PDF and print carry the note on every page.")}
        </p>
      )}
      {verborgen && <p className="notice">{t(`Diese Fassung enthält den vertraulichen Teil nicht, weil er vor ${ROLE_LABEL[view]} verborgen ist.`, `This version does not contain the confidential part because it is hidden from ${ROLE_LABEL[view]}.`)}</p>}
      {fehler && <p className="tk-error" role="alert">{fehler}</p>}
      {r.html
        ? <VertragPapier key={view} start="pages" leiste vermerk={vermerk} logoPath={ws.org.logo_path} accent={ws.org.brand_color} html={r.html} annexHtml={r.annexHtml}
            eyebrow={kopf} defs={quelle.defs ? [...quelle.defs] : undefined} timeZone={tz} lang={quelle.lang} marks={quelle.marks} />
        : <p className="muted">{t("Dieser Vertrag hat noch keinen Text.", "This contract has no text yet.")}</p>}
      {druck && r.html && createPortal(
        <div className="tk-vv-druck" aria-hidden="true">
          <SeitenDruck html={r.html} annexHtml={r.annexHtml} marks={quelle.marks} lang={quelle.lang} timeZone={tz} title={kopf} accent={ws.org.brand_color} vermerk={vermerk} />
        </div>,
        document.body,
      )}
    </div>
  );
}

/** Sichten eines Vertrags: Agentur, Marke und Creator, soweit sie Partei sind (wie die Druckansicht). */
export function vertragsSichten(c: { required_signers: readonly ContractRole[]; brand_id?: string | null; talent_id?: string | null }): ContractRole[] {
  return ["agency", ...(c.required_signers.includes("brand") || c.brand_id ? ["brand" as const] : []), ...(c.talent_id || c.required_signers.includes("talent") ? ["talent" as const] : [])];
}

/** Entwurf aus dem Editor-Stand oder dem gespeicherten Vertrag (renderDraft je Sicht, vertraulicher Teil je Rolle). */
export function entwurfQuelle(o: {
  title: string; numberLabel: string | null; lang: Language; roles: readonly ContractRole[];
  input: Parameters<typeof renderDraft>[0]; hiddenFrom: readonly ContractRole[]; required: readonly ContractRole[]; marks: readonly SignatureMark[]; link?: string | null;
}): VorschauQuelle {
  return {
    title: o.title, numberLabel: o.numberLabel, lang: o.lang, roles: o.roles, hiddenFrom: o.hiddenFrom, marks: o.marks, defs: o.input.defs, art: "draft", link: o.link ?? null,
    hasAnnex: !!o.input.annex?.replace(/<[^>]*>/g, "").trim(),
    render: (view) => renderDraft(o.input, view, [...o.hiddenFrom], [...o.required]),
  };
}

/** Vorlage mit Beispielwerten (Vorlagenliste, Vorlagen-Editor, Baukasten, Neuanlage): Rechtsraum und Währung der Agentur. */
export function vorlageQuelle(v: VorlageFuerVorschau, org: OrgDaten | null | undefined): VorschauQuelle {
  const beispiel = vorlagenBeispiel(org, v.language, org?.contractSettings.number_format ?? "V-{NNNN}", new Date().getFullYear());
  const input = { body: v.body_html, annex: v.annex_html, defs: [...v.variables], values: exampleValues(v.variables), context: beispiel.context, locale: beispiel.locale, currency: beispiel.currency };
  return {
    title: v.name, numberLabel: null, lang: v.language, roles: CONTRACT_ROLES, hiddenFrom: v.annex_hidden_from, defs: v.variables, art: "template", link: v.link ?? null,
    hasAnnex: !!v.annex_html?.replace(/<[^>]*>/g, "").trim(),
    marks: CONTRACT_ROLES.map((role) => ({ role, signed: false })),
    render: (view) => renderDraft(input, view, [...v.annex_hidden_from], [...v.required_signers]),
  };
}

export interface VorlageFuerVorschau {
  name: string;
  language: Language;
  body_html: string;
  annex_html: string | null;
  variables: readonly CustomVariableDef[];
  annex_hidden_from: readonly ContractRole[];
  required_signers: readonly ContractRole[];
  link?: string | null;
}

/** Quelle aus einem geladenen Vertrag: Entwurf wie die Versandvorschau, versendet aus der eingefrorenen Fassung. */
export function vertragQuelle(ws: StaffWorkspace, d: VertragDetail, org: OrgDaten, link?: string | null): VorschauQuelle | null {
  const c = d.contract;
  const roles = vertragsSichten(c);
  const numberLabel = c.contract_number_label ?? (c.contract_number != null ? contractNumberLabel(c.contract_number) : null);
  if (c.status === "draft") {
    const today = todayIn(ws.org.timezone);
    const ctx = kontextFuer(ws, d, org, { date: today });
    const money = geldFormat(org, d);
    // Dokument der Marke (0071): Unterschriftsseite aus der angemeldeten Datei (wie beim Versand)
    const cover = c.document_kind === "brand_pdf" ? renderCover({ language: c.language, roles: c.required_signers, doc: documentOf(c), context: ctx, ...money }) : null;
    const input = cover ? { body: cover.body, annex: null, defs: cover.defs, values: cover.values, context: ctx, ...money }
      : { body: c.body_html, annex: c.annex_html, defs: c.variable_defs, values: c.variables, context: ctx, ...money };
    // Unterschriftsfelder der Agentur entstehen erst beim Versand: im Entwurf wie im Editor als offene Felder
    const agency = d.signers.some((s) => s.role === "agency") || !c.required_signers.includes("agency") ? [] : Array.from({ length: c.agency_signatures ?? 1 }, (_, i) => ({
      role: "agency" as ContractRole, slot: i + 1, capacity: "party" as const, name: i === 0 ? org.legal?.representative_name ?? "" : "", signed_at: null,
      signing_name: null, signing_position: null, signature_data: null,
    }));
    return entwurfQuelle({ title: c.title, numberLabel: c.contract_number_label, lang: c.language, roles, input, hiddenFrom: cover ? [] : c.annex_hidden_from,
      required: c.required_signers, marks: marksOf([...d.signers, ...agency]), link });
  }
  if (c.origin === "pdf_upload" || !c.rendered_html) return null;
  const agencyName = org.settings.legal_name ?? ws.org.name;
  return {
    title: c.title, numberLabel, lang: c.language, roles, hiddenFrom: c.annex_hidden_from, defs: c.variable_defs, art: "contract", link: link ?? null,
    hasAnnex: !!c.rendered_annex_html?.replace(/<[^>]*>/g, "").trim(),
    marks: marksOf(d.signers),
    render: (view) => ({ html: c.rendered_html, annexHtml: view !== "agency" && c.annex_hidden_from.includes(view) ? null : c.rendered_annex_html }),
    ausfertigung: (view) => ausfertigungAusDetail(d, view, agencyName, ws.org.timezone),
  };
}

/**
 * Vorschau eines gespeicherten Vertrags nach Kennung (Liste, Detail, Kooperation, Creator-Profil, Markendetail, Suche). Lädt
 * erst beim Öffnen; ohne das Recht „Beträge sehen“ kein Vertragstext (wie die Vertragsseiten, Praxistest K-037).
 */
export function VertragVorschau({ contractId, onClose, link = true }: { contractId: string | null; onClose: () => void; link?: boolean }) {
  const ws = useStaffWorkspace();
  const sichtbar = useBetraegeSichtbar();
  const open = !!contractId;
  const { detail, org } = useVertragDaten(ws, contractId ?? "", open && sichtbar);
  const d = detail.data;
  const quelle = open && sichtbar && d && org.data ? vertragQuelle(ws, d, org.data, link ? `/agentur/vertraege/${d.contract.id}` : null) : null;
  const meldung = !sichtbar ? <p className="notice">{t("Verträge enthalten Honorare und Konditionen.", "Contracts contain fees and terms.")} {betraegeAus()}</p>
    : detail.error || org.error ? <p className="tk-error" role="alert">{describeError(detail.error ?? org.error).text}</p>
    : detail.isPending || org.isPending ? <p className="muted" role="status">{t("Wird geladen …", "Loading …")}</p>
    : !d ? <p className="notice">{t("Diesen Vertrag gibt es nicht oder er ist für dein Konto nicht freigegeben.", "This contract does not exist or is not available to your account.")}</p>
    : (
      <div className="stack">
        <p className="notice">{t("Extern unterschriebener Vertrag: Das PDF liegt in der Detailansicht.", "Externally signed contract: the PDF is in the detail view.")}</p>
        <div className="row"><Link className="btn" to={`/agentur/vertraege/${d.contract.id}`} replace>{t("Vertrag öffnen", "Open contract")}</Link></div>
      </div>
    );
  return <VorschauDialog open={open} onClose={onClose} title={d ? t(`Vorschau: ${d.contract.title}`, `Preview: ${d.contract.title}`) : t("Vorschau", "Preview")} quelle={quelle} meldung={meldung} />;
}

/** Vorschau einer gespeicherten Vorlage mit Beispielwerten nach Kennung (Vorlagenliste, gewählte Vorlage beim Neuanlegen). */
export function VorlagenVorschau({ templateId, onClose }: { templateId: string | null; onClose: () => void }) {
  const ws = useStaffWorkspace();
  const orgId = ws.org.org_id;
  const open = !!templateId;
  const tpl = useQuery({ queryKey: ["contract_templates", orgId, templateId ?? ""], queryFn: () => loadVorlage(api, orgId, templateId!), enabled: open });
  const org = useQuery({ queryKey: ["org_daten", orgId], queryFn: () => loadOrgDaten(api, orgId), enabled: open });
  const v = tpl.data;
  const quelle = open && v && (org.data || org.isError) ? vorlageQuelle({ ...v, link: `/agentur/vertraege/vorlagen/${v.id}` }, org.data) : null;
  const meldung = tpl.error ? <p className="tk-error" role="alert">{describeError(tpl.error).text}</p>
    : tpl.isPending || org.isPending ? <p className="muted" role="status">{t("Wird geladen …", "Loading …")}</p>
    : <p className="notice">{t("Diese Vorlage gibt es nicht oder sie ist für dein Konto nicht freigegeben.", "This template does not exist or is not available to your account.")}</p>;
  return <VorschauDialog open={open} onClose={onClose} title={v ? t(`Vorschau: ${v.name}`, `Preview: ${v.name}`) : t("Vorschau", "Preview")} quelle={quelle} meldung={meldung} />;
}
